import Immutable from 'immutable';
import { makeObservable, observable, action, runInAction } from 'mobx';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { searchAccountListByIds } from 'src_server/types/accountData';

export type ImmutableItemType = Immutable.Map<string, unknown>;
export type DataType = Immutable.List<ImmutableItemType>;

type KycStatusFilterType = 'All' | 'Requested' | 'Passed' | 'Failed';
type SortDirectionType = 'reason-asc' | 'reason-desc' | 'date-desc';

export interface KycFilterOptionType {
    value: KycStatusFilterType;
    label: string;
}

interface CustomerInfoType {
    firstName: string;
    surname: string;
    kycStatus: searchAccountListByIds.SuccessResponseType['results'][number]['properties']['kycStatus'];
}

export interface RowDataType {
    id: string;
    accountId: number;
    createdAt: string | undefined;
    finalizedAt: string | undefined;
    overall: string;
    triggerReason: string;
    details: unknown;
}

export const DEFAULT_KYC_FILTER: KycFilterOptionType = { value: 'All', label: 'All' };

export const KYC_FILTER_OPTIONS: KycFilterOptionType[] = [
    { value: 'All', label: 'All' },
    { value: 'Requested', label: 'Requested' },
    { value: 'Passed', label: 'Passed' },
    { value: 'Failed', label: 'Failed' },
];

export interface ActionCallbacksType {
    onCancel: (item: unknown) => void;
    onAssign: (item: unknown) => void;
    onUnassign: (item: unknown) => void;
}

const ACTION_MAP: Record<string, keyof ActionCallbacksType> = {
    unassign: 'onUnassign',
    assign: 'onAssign',
    cancel: 'onCancel',
};

export class IdMissionVerificationsState {
    @observable public expandedRows: Array<string> = [];
    @observable public kycFilter: KycFilterOptionType = DEFAULT_KYC_FILTER;
    @observable public sortDirection: SortDirectionType = 'date-desc';
    @observable public customerData: Map<number, CustomerInfoType> = new Map();

    private fetchedAccountIds: Set<number> = new Set();

    public constructor(private trpcClient: TrpcClient) {
        makeObservable(this);
    }

    private getAccountIdFromItem(item: ImmutableItemType): number | undefined {
        const value = item.get('accountId');
        if (value === undefined) {
            return undefined;
        }
        const parsed = parseInt(String(value), 10);
        return isNaN(parsed) ? undefined : parsed;
    }

    private getCustomer(accountId: number): CustomerInfoType | undefined {
        return this.customerData.get(accountId);
    }

    private isValidValue(value: unknown): value is string | number {
        return value !== undefined && value !== null && value !== '';
    }

    public getRowData = (item: ImmutableItemType): RowDataType => {
        const id = String(item.get('id') ?? '');
        const accountId = this.getAccountIdFromItem(item) ?? 0;

        const createdAtValue = item.getIn(['details', 'created_at']) ?? item.get('createdAt');
        const createdAt = this.isValidValue(createdAtValue) ? String(createdAtValue) : undefined;

        const finalizedAtValue = item.getIn(['details', 'finalized_at']) ?? item.get('finalizedAt');
        const finalizedAt = this.isValidValue(finalizedAtValue) ? String(finalizedAtValue) : undefined;

        const overallValue = item.getIn(['details', 'progress', 'overall']) ?? item.getIn(['progress', 'overall']);
        const overall = this.isValidValue(overallValue) ? String(overallValue) : '-';

        const triggerValue = item.getIn(['details', 'trigger']);
        const triggerReason = this.isValidValue(triggerValue) ? String(triggerValue) : '-';

        const details = item.get('details') ?? item;

        return { id, accountId, createdAt, finalizedAt, overall, triggerReason, details };
    };

    public ensureCustomerDataFetched = (data: DataType): void => {
        const newAccountIds: number[] = [];

        data.forEach((item) => {
            if (item === undefined) {
                return;
            }
            const accountId = this.getAccountIdFromItem(item);
            if (accountId !== undefined && !this.fetchedAccountIds.has(accountId)) {
                newAccountIds.push(accountId);
                this.fetchedAccountIds.add(accountId);
            }
        });

        if (newAccountIds.length > 0) {
            void this.fetchCustomerData(newAccountIds);
        }
    };

    @action private fetchCustomerData = async (accountIds: number[]): Promise<void> => {
        if (accountIds.length === 0) {
            return;
        }

        try {
            const response = await this.trpcClient.client.accountData.searchAccountListByIds.mutate({
                ids: accountIds,
                include: ['id', 'properties.firstName', 'properties.surname', 'properties.kycStatus'],
            });

            if (response.responseStatus === 'success') {
                runInAction(() => {
                    response.data.results.forEach((item) => {
                        this.customerData.set(item.id, {
                            firstName: item.properties.firstName ?? '',
                            surname: item.properties.surname ?? '',
                            kycStatus: item.properties.kycStatus,
                        });
                    });
                });
            } else {
                console.error('fetchCustomerData API error:', response.status);
            }
        } catch (error) {
            console.error('fetchCustomerData error:', error);
        }
    };

    public toggleRow =
        (id: string) =>
        (e: React.MouseEvent): void => {
            e.preventDefault();
            runInAction(() => {
                if (this.expandedRows.includes(id)) {
                    this.expandedRows = this.expandedRows.filter((rowId) => rowId !== id);
                } else {
                    this.expandedRows = [...this.expandedRows, id];
                }
            });
        };

    @action public setKycFilter = (option: KycFilterOptionType | null): void => {
        if (option !== null) {
            this.kycFilter = option;
        }
    };

    @action public toggleSortDirection = (): void => {
        if (this.sortDirection === 'date-desc') {
            this.sortDirection = 'reason-asc';
        } else if (this.sortDirection === 'reason-asc') {
            this.sortDirection = 'reason-desc';
        } else {
            this.sortDirection = 'date-desc';
        }
    };

    @action public setDateSort = (): void => {
        this.sortDirection = 'date-desc';
    };

    public getFilteredAndSortedData = (data: DataType): DataType => {
        let filtered: DataType = data;

        if (this.kycFilter.value !== 'All') {
            const filteredResult = data.filter((item) => {
                if (item === undefined) {
                    return false;
                }
                const accountId = this.getAccountIdFromItem(item) ?? 0;
                const kycStatus = this.getCustomer(accountId)?.kycStatus;
                if (kycStatus === undefined) {
                    return false;
                }
                return kycStatus.toLowerCase() === this.kycFilter.value.toLowerCase();
            });
            filtered = Immutable.List<ImmutableItemType>(filteredResult.toArray());
        }

        const sortedResult = filtered.sort((a, b) => {
            if (this.sortDirection === 'date-desc') {
                const dateA = a.getIn(['details', 'created_at']) ?? a.get('createdAt');
                const dateB = b.getIn(['details', 'created_at']) ?? b.get('createdAt');
                const dateStrA = dateA === undefined || dateA === '' ? '' : String(dateA);
                const dateStrB = dateB === undefined || dateB === '' ? '' : String(dateB);
                return dateStrB.localeCompare(dateStrA);
            }

            const triggerA = a.getIn(['details', 'trigger']);
            const triggerB = b.getIn(['details', 'trigger']);
            const reasonA = triggerA === undefined || triggerA === '' ? '' : String(triggerA);
            const reasonB = triggerB === undefined || triggerB === '' ? '' : String(triggerB);
            const compare = reasonA.localeCompare(reasonB);
            return this.sortDirection === 'reason-asc' ? compare : -compare;
        });

        return Immutable.List<ImmutableItemType>(sortedResult.toArray());
    };

    public getCustomerName = (accountId: number): string => {
        const customer = this.getCustomer(accountId);
        if (customer !== undefined) {
            return `${customer.firstName} ${customer.surname}`;
        }
        return '-';
    };

    public getKycStatus = (accountId: number): CustomerInfoType['kycStatus'] | '-' => {
        const customer = this.getCustomer(accountId);
        if (customer !== undefined) {
            return customer.kycStatus;
        }
        return '-';
    };

    public isRowExpanded = (id: string): boolean => {
        return this.expandedRows.includes(id);
    };

    public doAction = (
        e: React.SyntheticEvent,
        item: Immutable.Map<string, unknown>,
        callbacks: ActionCallbacksType
    ): void => {
        const target = e.target;
        if (!(target instanceof HTMLSelectElement)) {
            return;
        }

        const callbackKey = ACTION_MAP[target.value];
        if (callbackKey !== undefined) {
            callbacks[callbackKey](item);
        }
    };
}
